import { CustomResponse } from '../model/CustomResponse';

export class HttpError extends Error {
  statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

export class JsonError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, JsonError.prototype);
  }
}

export const fetchProvider = (
  appName: string,
  shopRoute?: string,
  requestTimeout?: number,
) => {
  const customFetch = async <T>(
    url: RequestInfo,
    acceptedCodes?: number[],
    init?: RequestInit,
  ): Promise<CustomResponse<T>> => {
    const bffUrl = (shopRoute ?? '') + url;
    const headers: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Caller': appName,
      },
    };
    const response = await fetch(bffUrl, {
      ...init,
      ...headers,
    });
    if (!response.ok || (acceptedCodes && !acceptedCodes.includes(response.status))) {
      throw new HttpError(response.status, await response.text());
    }
    try {
      let responseText = await response.text();
      if (responseText === '') {
        responseText = '{}';
      }

      const returnedValue: CustomResponse<T> = {
        statusCode: response.status,
        responseBody: JSON.parse(responseText) as T,
      };
      return Promise.resolve(returnedValue);
    } catch (e) {
      if (e instanceof Error) {
        throw new JsonError(e.message);
      } else {
        throw new JsonError(e as string);
      }
    }
  };

  //add some convenience methods
  customFetch.get = async <T>(
    url: RequestInfo,
    acceptedCodes?: number[],
    init: RequestInit = { method: 'GET' },
  ) => customFetch<T>(url, acceptedCodes, init);

  customFetch.delete = async <T>(
    url: RequestInfo,
    acceptedCodes?: number[],
    init: RequestInit = { method: 'DELETE' },
  ) => customFetch<T>(url, acceptedCodes, init);

  customFetch.post = async <T>(
    url: RequestInfo,
    body: object,
    acceptedCodes?: number[],
    init: RequestInit = {
      method: 'POST',
      body: JSON.stringify(body),
    },
  ) => customFetch<T>(url, acceptedCodes, init);

  customFetch.put = async <T>(
    url: RequestInfo,
    body: object,
    acceptedCodes?: number[],
    init: RequestInit = {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  ) => customFetch<T>(url, acceptedCodes, init);

  customFetch.patch = async <T>(
    url: RequestInfo,
    body: object,
    acceptedCodes?: number[],
    init: RequestInit = {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  ) => customFetch<T>(url, acceptedCodes, init);
  return customFetch;
};
